import { useEffect, useMemo, useState } from "react";
import { useSidebar } from "../../hooks/useSidebar";
import { apiFetch } from "../../services/apiClient";
import { propiedadesApi } from "../../services/propiedades.api";
import { inventarioApi } from "../../services/inventario.api";
import type { RegistroFinanza, OpcionBasica } from "./finanzasSection";
import type { AseoRegistro } from "./sectionAseos";
import type { ArriendoRegistro } from "./sectionArriendos";
import type { ProductoRegistro } from "./sectionProductos";
import dayjs from "dayjs";
import { ahoraChile } from "../../utils/fechaChile";

export function useFinanzasEmpresaLogic() {
  const [loading, setLoading] = useState(false);
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [rows, setRows] = useState<RegistroFinanza[]>([]);

  // Solo getters: los setters de estos estados no se usan actualmente
  // (los datos se cargan vía endpoints específicos en cada sección).
  // `lavanderia` se eliminó en iter 23: la pestaña Lavandería pasó a ser
  // vista resumen ejecutiva y carga sus propios datos via searchLavados +
  // getGastosOperacionales.
  const [aseos] = useState<AseoRegistro[]>([]);
  const [arriendos] = useState<ArriendoRegistro[]>([]);

  const [usuarios, setUsuarios] = useState<OpcionBasica[]>([]);
  const [propiedades, setPropiedades] = useState<OpcionBasica[]>([]);
  const [productos] = useState<ProductoRegistro[]>([]);

  // `nombres` se eliminó (auditoría 2026-07-13): era un useState([]) sin
  // setter que alimentaba un Select siempre vacío en Productos — el filtro
  // "Producto" pasó a ser texto libre sobre producto_nombre.
  const [marcas, setMarcas] = useState<OpcionBasica[]>([]);
  const [bodegas, setBodegas] = useState<OpcionBasica[]>([]);
  const [bodeguitas, setBodeguitas] = useState<OpcionBasica[]>([]);

  const fetchSharedData = async () => {
    setLoading(true);
    const [users, props, brands, bodegasData, bodeguitasData] =
      await Promise.all([
        apiFetch<any>("/users/finanzas"),
        propiedadesApi.getAll(),
        inventarioApi.getMarcas(),
        propiedadesApi.getBodegas(),
        propiedadesApi.getBodeguitas(),
      ]);
    const propsRaw: any[] = Array.isArray(props?.data) ? props!.data : [];
    const bodegasRaw: any[] = Array.isArray(bodegasData?.data)
      ? bodegasData!.data
      : [];
    const bodeguitasRaw: any[] = Array.isArray(bodeguitasData?.data)
      ? bodeguitasData!.data
      : [];
    const mappedProperties = propsRaw.map((p) => ({
      id: p.id,
      nombre: p.nombrepropiedad,
    }));
    const mappedBodegas = bodegasRaw.map((b) => ({
      id: b.id,
      nombre: b.bodega,
    }));
    const mappedBodeguitas = bodeguitasRaw.map((b) => ({
      id: b.id,
      nombre: b.bodeguita,
    }));
    setBodegas(mappedBodegas);
    setBodeguitas(mappedBodeguitas);
    setUsuarios(users ?? []);
    setPropiedades(mappedProperties);
    setMarcas(brands?.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchSharedData();
  }, []);

  const sueldos = useMemo(
    () => rows.filter((r) => r.nombre?.toLowerCase().includes("sueldo")),
    [rows],
  );
  const gastos = useMemo(
    () => rows.filter((r) => !r.nombre?.toLowerCase().includes("sueldo")),
    [rows],
  );

  // --- CRUD real (backend) ---
  // POST /api/finanzas/empresa/expenses
  const createExpense = async (payload: Omit<RegistroFinanza, "id">) => {
    const r = await apiFetch<{ data: RegistroFinanza }>(
      "/finanzas/empresa/expenses",
      { method: "POST", body: JSON.stringify(payload) },
    );
    if (!r) throw new Error("Error creando expense");
    return r.data;
  };

  // PUT /api/finanzas/empresa/expenses/:id
  const updateExpense = async (
    id: string,
    payload: Partial<RegistroFinanza>,
  ) => {
    const r = await apiFetch<{ data: RegistroFinanza }>(
      `/finanzas/empresa/expenses/${id}`,
      { method: "PUT", body: JSON.stringify(payload) },
    );
    if (!r) throw new Error("Error actualizando expense");
    return r.data;
  };

  // DELETE /api/finanzas/empresa/expenses/:id
  const deleteExpense = async (id: string) => {
    const r = await apiFetch<{ ok: boolean }>(
      `/finanzas/empresa/expenses/${id}`,
      { method: "DELETE" },
    );
    if (!r) throw new Error("Error eliminando expense");
  };

  // --- wrappers que ya usa tu UI (sueldos/gastos) ---
  //
  // Round 2026-05-13 — Defense-in-depth para sueldos: el modal ya
  // declara los campos `rolDeAlojaFacil` y `pagadoPorRol` como hidden
  // con valores fijos, pero acá los forzamos OTRA vez al construir el
  // payload del backend. Razón: un sueldo POR DEFINICIÓN es egreso de
  // AF (AF paga, otro recibe). Si por cualquier razón el form viniera
  // con valores distintos (legacy data, bug de UI, edit de row antiguo
  // que tenía rol "recibe" mal guardado), normalizamos acá para que la
  // base de datos NUNCA tenga sueldos con rol distinto a "paga". Eso
  // simplifica todos los reportes downstream que asumen esta
  // invariante (panel /finanzas-empresa → Sueldos, finanzas-aseador,
  // exportXls, etc.).
  const normalizarSueldo = <T extends Partial<RegistroFinanza>>(r: T): T => ({
    ...r,
    rolDeAlojaFacil: "paga",
    pagadoPorRol: "administrador",
    pagadoPorUsuarioId: null,
    pagadoPorRecipientId: null,
    // `pagadoPor` (JSON PagoEntidad) ALSO has to be reescrita acá:
    // `submit()` en finanzasSection.tsx arma este objeto via
    // `buildPagoFromRol(values.pagadoPorRol, …)` ANTES de llegar a este
    // wrapper, y el backend prefiere `body.pagadoPor` sobre
    // `body.pagadoPorRol` (ver createExpenseEmpresa.controller.js:243).
    // Si el form arrastró un pagadoPor legacy con tipo distinto a 0
    // (ej. tipo=3 aseador) y normalizamos SOLO los campos UI, el JSON
    // ganaría al fallback de roles y el sueldo se persistiría con
    // origen incorrecto. Por eso reescribimos también el JSON acá.
    pagadoPor: {
      tipo: 0,
      nombre: "Aloja Fácil",
      usuario_id: null,
      recipient_id: null,
    },
  });

  const createSueldo = async (r: Omit<RegistroFinanza, "id">) => {
    const created = await createExpense(
      normalizarSueldo({ ...r, categoria: "sueldo" }),
    );
    setRows((prev) => [created, ...prev]);
    return created;
  };

  const updateSueldo = async (id: string, r: Partial<RegistroFinanza>) => {
    const updated = await updateExpense(id, normalizarSueldo(r));
    setRows((prev) => prev.map((x) => (x.id === id ? updated : x)));
  };

  const deleteSueldo = async (id: string) => {
    await deleteExpense(id);
    setRows((prev) => prev.filter((x) => x.id !== id));
  };

  const createGasto = async (r: Omit<RegistroFinanza, "id">) => {
    // No sobrescribir `categoria`: el formulario obliga al usuario a
    // elegir una categoría válida del ENUM (`compra`, `cuenta`,
    // `reparacion`, etc.) en finanzasSection.tsx. Antes acá pisábamos
    // con `"Gastos"`, que el backend lowercasea a `"gastos"` — valor que
    // NO está en el ENUM `categoria` de la tabla `gastos`, así que el
    // INSERT explotaba con "internal error" (Sequelize/Postgres rechazan
    // el ENUM inválido). Bug visible al usuario como toast genérico al
    // crear cualquier gasto.
    const created = await createExpense(r);
    setRows((prev) => [created, ...prev]);
    return created;
  };

  const updateGasto = async (id: string, r: Partial<RegistroFinanza>) => {
    const updated = await updateExpense(id, r);
    setRows((prev) => prev.map((x) => (x.id === id ? updated : x)));
  };

  const deleteGasto = async (id: string) => {
    await deleteExpense(id);
    setRows((prev) => prev.filter((x) => x.id !== id));
  };

  return {
    loading,
    sidebarOpen,
    toggleSidebar,
    // Refetch de los catálogos compartidos (usuarios, propiedades, marcas,
    // bodegas, bodeguitas). Lo usa el botón "Actualizar" del header de
    // /finanzas-empresa para refrescar también estos selects, además de los
    // cálculos por sección (que se re-disparan vía `refreshKey`).
    refetchShared: fetchSharedData,
    // catálogos
    usuarios,
    propiedades,
    // finanzas base
    sueldos,
    gastos,
    createSueldo,
    updateSueldo,
    deleteSueldo,
    createGasto,
    updateGasto,
    deleteGasto,
    // aseos
    aseos,
    // arriendos
    arriendos,
    // productos
    productos,
    marcas,
    bodegas,
    bodeguitas,
  };
}

type DateRange = { from?: string; to?: string };

const getCurrentMonthPeriod = () => ({
  from: ahoraChile().startOf("month").toISOString(),
  to: ahoraChile().endOf("month").toISOString(),
});

const clean = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(([_, v]) => {
      if (v == null) return false;
      if (typeof v === "string" && !v.trim()) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    }),
  );

const parseDateRange = (
  range?: [string, string] | null,
): DateRange | undefined => {
  if (!range || range.length !== 2) return;
  const [d1, d2] = range;
  return {
    from: dayjs(d1, "DD-MM-YYYY").startOf("day").toISOString(),
    to: dayjs(d2, "DD-MM-YYYY").endOf("day").toISOString(),
  };
};

// 🔹 Normaliza SOLO filtros de Aseos (convierte RangePicker → {from,to} y limpia vacíos)
export function normalizeAseosFilters(ui: Record<string, any>) {
  const f = { ...ui };
  if ("fechaInicio" in f)
    f.fechaInicio = parseDateRange(f.fechaInicio) || undefined;
  return clean(f);
}

export type AseosFilters = {
  usuarioId?: number;
  pagadoA?: number;
  propiedadId?: number;
  sector?: string | number;
  urgencia?: string | number;
  fechaInicio?: DateRange;
  observaciones?: string;
  monto?: { min?: number; max?: number };
  sueldoAseador?: { min?: number; max?: number };
  // Round 2026-05-11: filtro `bono` removido — el campo se eliminó de aseos.
  estado?: string | number;
};

export type SearchResponse<Row = any> = {
  rows: Row[];
  total: number;
  totals?: Record<string, number>;
};

// 🔹 Llama al backend de Aseos
export async function fetchAseos(
  filters: AseosFilters,
  opts: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
  },
): Promise<SearchResponse> {
  const body = {
    page: opts.page,
    pageSize: opts.pageSize,
    sortBy: opts.sortBy ?? "fecha",
    sortDir: opts.sortDir ?? "desc",
    period: getCurrentMonthPeriod(),
    filters: normalizeAseosFilters(filters),
  };

  const res = await apiFetch<SearchResponse>("/finanzas/aseos/search", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res) throw new Error("Error al cargar Aseos");
  return res;
}
